<template>
  <div class="widget-card-compact">
    <StepHeader
      :step="2"
      :total="3"
      :name="stepTwoLabel"
      :title="formTitle"
      subtitle="Te enviamos la cotización a este correo."
      :context="vehicleContext"
    />

    <div class="widget-fields-compact">
      <DriverTextField
        v-if="!demographicsOnly"
        :model-value="formData.fullName"
        label="Nombre completo"
        placeholder="Ingresa tu nombre"
        full-width
        @update:model-value="updateField('fullName', $event)"
      />

      <template v-if="showDemographicsFields">
        <DriverGenderField
          :gender="formData.gender"
          @update:gender="updateField('gender', $event)"
        />
        <DriverBirthDateField
          :birth-date="formData.birthDate"
          @update:birth-date="updateField('birthDate', $event)"
        />
      </template>

      <template v-if="!demographicsOnly">
        <DriverTextField
          :model-value="formData.email"
          type="email"
          label="Correo electrónico"
          placeholder="correo@ejemplo.com"
          :invalid="Boolean(formData.email) && !isValidEmail(formData.email)"
          error-message="Correo inválido"
          @update:model-value="updateField('email', $event)"
        />
        <DriverTextField
          :model-value="formData.whatsapp"
          :label="contactOnly ? 'Teléfono' : 'WhatsApp'"
          :placeholder="contactOnly ? '55 1234 5678' : '+52 55 1234 5678'"
          :invalid="Boolean(formData.whatsapp) && !isValidPhone(formData.whatsapp)"
          error-message="Teléfono inválido"
          @update:model-value="updateField('whatsapp', $event)"
        />
      </template>

      <div v-if="!contactOnly && !demographicsOnly" class="field-group field-group-full">
        <label class="field-label-compact">
          Código postal<span class="required-asterisk">*</span>
        </label>
        <WidgetPostalCodeField
          :model-value="formData.postalCodeDisplay"
          placeholder="03810"
          size="large"
          @select="postalUpdates.handleSelect"
          @clear="postalUpdates.handleClear"
          @invalidate="postalUpdates.handleInvalidate"
          @update:model-value="updateField('postalCodeDisplay', $event)"
        />
      </div>

      <DriverPrivacyConsent
        v-if="!demographicsOnly"
        :consent="formData.privacyConsent"
        :show-error="!formData.privacyConsent && hasAttemptedSubmit"
        @update:consent="updateField('privacyConsent', $event)"
        @open-notice="showPrivacyDialog = true"
      />
    </div>

    <DriverActions
      :is-complete="isComplete"
      :is-loading="isLoading"
      @back="emit('back')"
      @submit="handleSubmit"
    />

    <PrivacyNoticeDialog v-model="showPrivacyDialog" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { getActivePinia } from 'pinia';
import { useQuoteStore } from '@/stores/quoteStore';
import PrivacyNoticeDialog from './PrivacyNoticeDialog.vue';
import { useFormValidation } from '@/composables/useFormValidation';
import WidgetPostalCodeField from '@/components/widget/WidgetPostalCodeField.vue';
import StepHeader from './vehicleSelection/StepHeader.vue';
import DriverActions from './driverDataForm/DriverActions.vue';
import DriverBirthDateField from './driverDataForm/DriverBirthDateField.vue';
import DriverGenderField from './driverDataForm/DriverGenderField.vue';
import DriverPrivacyConsent from './driverDataForm/DriverPrivacyConsent.vue';
import DriverTextField from './driverDataForm/DriverTextField.vue';
import type { DriverFormData } from './driverDataForm/types';
import { useDriverFormState } from './driverDataForm/useDriverFormState';
import { usePostalCodeUpdates } from './driverDataForm/usePostalCodeUpdates';

interface Props {
  formData: DriverFormData;
  isLoading?: boolean;
  /** Modo reducido: solo nombre, teléfono, email (+ privacidad). */
  contactOnly?: boolean;
  /**
   * Minimal conductor gate for Chubb: only gender + birthDate.
   * Mutually exclusive with full contact fields.
   */
  demographicsOnly?: boolean;
  /** Force gender + birth even in contactOnly (Chubb + xcf). */
  requireDemographics?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isLoading: false,
  contactOnly: false,
  demographicsOnly: false,
  requireDemographics: false,
});

const emit = defineEmits<{
  update: [field: string, value: unknown];
  back: [];
  submit: [];
}>();

const { isValidEmail, isValidPhone } = useFormValidation();
const { showDemographicsFields, formTitle, stepTwoLabel, isComplete } = useDriverFormState(props);
const postalUpdates = usePostalCodeUpdates(emit);

// Selected vehicle as step context; hidden when there is no store or no selection.
const quoteStore = getActivePinia() ? useQuoteStore() : null;
const vehicleContext = computed(() => {
  const { year, brand, model } = quoteStore?.quoteData ?? {};
  if (!year || !brand || !model) return undefined;
  return `${year} ${brand.name} ${model.name}`;
});
const showPrivacyDialog = ref(false);
const hasAttemptedSubmit = ref(false);

const updateField = (field: string, value: unknown) => {
  emit('update', field, value);
};

const handleSubmit = (): void => {
  hasAttemptedSubmit.value = true;
  if (isComplete.value) {
    emit('submit');
  }
};
</script>

<style scoped src="./driverDataForm/fields.css"></style>
<style scoped>
/* Flat surface: hosts often mount without a background (showBackground: false). */
.widget-card-compact {
  background: var(--ui-surface);
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-panel);
  padding: clamp(16px, 3vw, 28px);
  box-sizing: border-box;
  width: 100%;
  max-width: 680px;
  margin: 0 auto;
  min-height: auto;
  display: flex;
  flex-direction: column;
  position: relative;
}

.widget-fields-compact {
  display: grid;
  grid-template-columns: 1fr;
  gap: 18px;
  margin-bottom: 28px;
}

@media (min-width: 640px) {
  .widget-fields-compact {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 639px) {
  .widget-card-compact {
    padding: 20px 16px;
  }
}

.widget-compact-mode .widget-card-compact {
  padding: 16px !important;
}
</style>
